import { CONTEXT_SECTION_TITLES } from '@lixionary-jira/domain';
import { CircleCheck, CircleDashed } from 'lucide-react';
import { Card } from '../../../ui/misc';
import type { ImplDetail } from './types';

export function ReadinessChecklist({ i }: { i: ImplDetail }) {
  const criteria = i.ticket.acceptanceCriteria;
  const ev = i.evidence;
  const contextOk = i.contextVersions
    .filter((c) => c.section !== 'deployment')
    .every((c) => !c.currentVersionId || c.current);
  const checks: [string, boolean, string][] = [
    [
      `Current acceptance (scope rev ${i.ticket.scopeRevision})`,
      !!i.acceptance && !i.acceptance.revokedAt && i.scopeRevision === i.ticket.scopeRevision,
      i.acceptance?.displayId ?? 'None',
    ],
    [
      'Context acknowledged',
      contextOk,
      i.contextVersions
        .map((c) => `${CONTEXT_SECTION_TITLES[c.section]} ${c.number ? `v${c.number}` : '—'}`)
        .join(' · '),
    ],
    [
      'Exact revision recorded',
      !!ev && /^[0-9a-f]{7,64}$/i.test(ev.sourceRevision),
      ev?.sourceRevision.slice(0, 12) ?? 'Missing',
    ],
    ...criteria.map((c, idx): [string, boolean, string] => {
      const r = ev?.criteriaResults.find((x) => x.criterionIndex === idx);
      return [
        `Criterion ${idx + 1}: ${c}`,
        !!r && r.outcome !== 'not_met' && !!r.observation.trim(),
        r ? r.outcome.replace('_', ' ') : 'Missing evidence',
      ];
    }),
    [
      'Tests or approved exceptions',
      !!ev &&
        (ev.tests.length > 0 || i.exceptions.some((e) => e.testName === '*')) &&
        ev.tests.every(
          (t) => t.result === 'passed' || i.exceptions.some((e) => e.testName === t.name && !e.revokedAt),
        ),
      ev ? `${ev.tests.filter((t) => t.result === 'passed').length}/${ev.tests.length} passed` : 'Missing',
    ],
    [
      'Delivery and rollback considerations',
      !!ev && !!ev.deploymentConsiderations.trim() && !!ev.rollbackConsiderations.trim(),
      ev ? 'Recorded' : 'Missing',
    ],
  ];

  return (
    <Card
      title="Readiness checklist"
      subtitle="All items are required before Pending release. Readiness does not authorize deployment."
    >
      <ul className="checklist">
        {checks.map(([label, ok, note]) => (
          <li key={label}>
            {ok ? (
              <CircleCheck size={16} color="var(--color-success)" aria-label="Complete" />
            ) : (
              <CircleDashed size={16} color="var(--color-muted-soft)" aria-label="Incomplete" />
            )}
            <span style={{ flex: 1 }}>{label}</span>
            <span className="small muted">{note}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
